import { Button } from "@bcc-ui/ui-components";
import classNames from "classnames";
import * as React from "react";
import { useTranslation } from "react-i18next";

import { KnowledgeBasePageTree } from "./KnowledgeBasePageTree";
import styles from "./KnowledgeBaseView.scss";
import Chevron from "components/icons/Chevron";
import { Lang } from "components/icons/Lang";
import { SidebarToggle } from "components/icons/SidebarToggle";
import { Logo } from "components/logo/Logo";
import { KnowledgeBaseArticle, knowledgeBaseService } from "services/news/KnowledgeBaseService";
import { getLanguageTitleByCode } from "services/product-manuals/productManualLanguageRepository";
import { useIsDarkThemeActive, useTheme } from "utils/useTheme";

const SIDEBAR_MIN_WIDTH = 200;
const SIDEBAR_MAX_WIDTH = 600;
const SIDEBAR_DEFAULT_WIDTH = 320;

const SUPPORTED_KNOWLEDGEBASE_LANGUAGES = ["en", "de", "ja"];

export interface SidebarRef {
    expandToPage: (ancestorIds: string[]) => Promise<void>;
    startDragging: () => void;
}

interface Props {
    onLogoClick: () => void;
    rootPages: { [key: string]: KnowledgeBaseArticle[] };
    rootPageTitles: { [key: string]: string };
    selectedLanguage: string;
    onLanguageChange: (language: string) => void;
    selectedPage?: KnowledgeBaseArticle;
    onArticleClick: (article: KnowledgeBaseArticle) => void;
}

export const KnowledgeBaseSidebar = React.forwardRef<SidebarRef, Props>(
    (
        { onLogoClick, rootPages, rootPageTitles, selectedLanguage, onLanguageChange, selectedPage, onArticleClick },
        ref
    ) => {
        const { t } = useTranslation();
        const theme = useTheme();
        const isDarkMode = useIsDarkThemeActive();
        const [showLanguageSelector, setShowLanguageSelector] = React.useState<boolean>(false);
        const [isCollapsed, setIsCollapsed] = React.useState<boolean>(false);
        const [expandedSections, setExpandedSections] = React.useState<Set<string>>(new Set());
        const [expandedPages, setExpandedPages] = React.useState<Set<string>>(new Set());
        const [childrenCache, setChildrenCache] = React.useState<{ [key: string]: KnowledgeBaseArticle[] }>({});
        const childrenCacheRef = React.useRef<{ [key: string]: KnowledgeBaseArticle[] }>({});
        const childrenBeingFetched = React.useRef<Set<string>>(new Set());
        const [width, setWidth] = React.useState<number>(SIDEBAR_DEFAULT_WIDTH);
        const [isDragging, setIsDragging] = React.useState<boolean>(false);
        const languageSelectorRef = React.useRef<HTMLDivElement>(null);
        const observerRef = React.useRef<IntersectionObserver | null>(null);

        React.useImperativeHandle(
            ref,
            () => ({
                expandToPage: async (ancestorIds: string[]) => {
                    setIsCollapsed(false);
                    const rootSectionIds = Object.keys(rootPages);
                    setExpandedSections(new Set(rootSectionIds.filter((id) => ancestorIds.includes(id))));
                    setExpandedPages(new Set(ancestorIds));

                    const missingAncestors = ancestorIds.filter((id) => !childrenCacheRef.current[id]);
                    if (missingAncestors.length > 0) {
                        const results = await Promise.allSettled(
                            missingAncestors.map((id) => knowledgeBaseService.fetchPageChildren(selectedLanguage, id))
                        );

                        const fetchedEntries: { [key: string]: KnowledgeBaseArticle[] } = {};
                        results.forEach((result, index) => {
                            const ancestorId = missingAncestors[index];
                            fetchedEntries[ancestorId] = result.status === "fulfilled" ? result.value.childPages : [];
                        });
                        setChildrenCache((prev) => ({ ...prev, ...fetchedEntries }));
                    }
                },
                startDragging: () => setIsDragging(true),
            }),
            [rootPages, selectedLanguage]
        );

        React.useEffect(() => {
            const handleMouseMove = (e: MouseEvent) => {
                if (isDragging) {
                    const newWidth = Math.max(SIDEBAR_MIN_WIDTH, Math.min(SIDEBAR_MAX_WIDTH, e.clientX));
                    setWidth(newWidth);
                }
            };

            const handleMouseUp = () => {
                setIsDragging(false);
            };

            if (isDragging) {
                document.addEventListener("mousemove", handleMouseMove);
                document.addEventListener("mouseup", handleMouseUp);
            }

            return () => {
                document.removeEventListener("mousemove", handleMouseMove);
                document.removeEventListener("mouseup", handleMouseUp);
            };
        }, [isDragging]);

        React.useLayoutEffect(() => {
            childrenCacheRef.current = childrenCache;
        }, [childrenCache]);

        React.useEffect(() => {
            observerRef.current = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            const pageId = entry.target.getAttribute("data-page-id");
                            if (
                                pageId &&
                                !childrenCacheRef.current[pageId] &&
                                !childrenBeingFetched.current.has(pageId)
                            ) {
                                childrenBeingFetched.current.add(pageId);

                                knowledgeBaseService
                                    .fetchPageChildren(selectedLanguage, pageId)
                                    .then((response) => {
                                        setChildrenCache((prev) => ({ ...prev, [pageId]: response.childPages }));
                                    })
                                    .catch(() => {
                                        setChildrenCache((prev) => ({ ...prev, [pageId]: [] }));
                                    })
                                    .finally(() => {
                                        childrenBeingFetched.current.delete(pageId);
                                    });
                            }
                        }
                    });
                },
                { threshold: 0.1 }
            );

            const observeElements = () => {
                const elements = document.querySelectorAll("[data-page-id]");
                elements.forEach((element) => {
                    if (observerRef.current) {
                        observerRef.current.observe(element);
                    }
                });
            };

            const timerId = setTimeout(observeElements, 100);

            return () => {
                clearTimeout(timerId);
                observerRef.current?.disconnect();
            };
        }, [expandedPages, expandedSections, childrenCache, selectedLanguage]);

        React.useEffect(() => {
            const handleClickOutside = (event: MouseEvent) => {
                if (languageSelectorRef.current && !languageSelectorRef.current.contains(event.target as Node)) {
                    setShowLanguageSelector(false);
                }
            };

            if (showLanguageSelector) {
                document.addEventListener("mousedown", handleClickOutside);
            }

            return () => {
                document.removeEventListener("mousedown", handleClickOutside);
            };
        }, [showLanguageSelector]);

        const handleSectionToggle = (sectionId: string) => {
            setExpandedSections((prev) => {
                const newExpanded = new Set(prev);
                if (newExpanded.has(sectionId)) {
                    newExpanded.delete(sectionId);
                } else {
                    newExpanded.add(sectionId);
                }
                return newExpanded;
            });
        };

        const handleCategoryToggle = async (pageId: string) => {
            const newExpanded = new Set(expandedPages);
            if (newExpanded.has(pageId)) {
                newExpanded.delete(pageId);
            } else {
                newExpanded.add(pageId);

                if (!childrenCacheRef.current[pageId]) {
                    try {
                        const childrenResponse = await knowledgeBaseService.fetchPageChildren(selectedLanguage, pageId);
                        setChildrenCache((prev) => ({ ...prev, [pageId]: childrenResponse.childPages }));
                    } catch {
                        setChildrenCache((prev) => ({ ...prev, [pageId]: [] }));
                    }
                }
            }
            setExpandedPages(newExpanded);
        };

        return (
            <div
                className={classNames(styles.sidebar, {
                    [styles.collapsed]: isCollapsed,
                })}
                style={{ width: isCollapsed ? 20 : width }}
            >
                <div className={styles.sidebarHeader}>
                    {!isCollapsed && (
                        <div onClick={onLogoClick} className={styles.logoContainer}>
                            <Logo
                                isWhiteLogo={isDarkMode}
                                text={{ title: "Blancco", subtitle: t("KnowledgeBase.logoSubText") }}
                            />
                        </div>
                    )}
                    <button onClick={() => setIsCollapsed(!isCollapsed)} className={styles.collapseButton}>
                        <SidebarToggle color={theme.iconFillColor} rotate={isCollapsed} />
                    </button>
                </div>

                {!isCollapsed && (
                    <div className={styles.sidebarContent}>
                        {Object.keys(rootPages).length > 0 &&
                            Object.entries(rootPages).map(([rootPageId, children]) => {
                                const isExpanded = expandedSections.has(rootPageId);
                                return (
                                    <div key={rootPageId} className={styles.rootSection}>
                                        <div
                                            onClick={() => handleSectionToggle(rootPageId)}
                                            className={styles.sectionHeader}
                                        >
                                            <Chevron
                                                color={theme.iconFillColor}
                                                width={20}
                                                height={20}
                                                className={classNames(styles.chevronIcon, {
                                                    [styles.expanded]: isExpanded,
                                                    [styles.collapsed]: !isExpanded,
                                                })}
                                            />
                                            <span className={styles.pageTitleText}>
                                                {rootPageTitles[rootPageId] || t("Common.loading")}
                                            </span>
                                        </div>
                                        {isExpanded && (
                                            <div>
                                                {children.map((page) => (
                                                    <React.Fragment key={page.id}>
                                                        <KnowledgeBasePageTree
                                                            page={page}
                                                            depth={0}
                                                            expandedPages={expandedPages}
                                                            childrenCache={childrenCache}
                                                            selectedPage={selectedPage}
                                                            onCategoryToggle={handleCategoryToggle}
                                                            onArticleClick={onArticleClick}
                                                        />
                                                    </React.Fragment>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                    </div>
                )}

                {!isCollapsed && (
                    <div ref={languageSelectorRef} className={styles.languageSelector}>
                        {showLanguageSelector && (
                            <div className={styles.languageDropdown}>
                                {SUPPORTED_KNOWLEDGEBASE_LANGUAGES.map((lang) => (
                                    <div
                                        key={lang}
                                        onClick={() => {
                                            onLanguageChange(lang);
                                            setShowLanguageSelector(false);
                                        }}
                                        className={styles.languageOption}
                                    >
                                        <span>{getLanguageTitleByCode(lang)}</span>
                                        {selectedLanguage === lang && <span>✓</span>}
                                    </div>
                                ))}
                            </div>
                        )}
                        <Button
                            onClick={() => setShowLanguageSelector(!showLanguageSelector)}
                            className={styles.languageButton}
                            icon={<Lang />}
                            variant={"Tertiary"}
                        >
                            {t("KnowledgeBase.languageSelect")}
                        </Button>
                    </div>
                )}
            </div>
        );
    }
);
